import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { ChevronDown } from "lucide-react";

export default function Privacy() {
  const [expanded, setExpanded] = React.useState(null);

  const toggleSection = (id) => {
    setExpanded(expanded === id ? null : id);
  };

  const sections = [
    {
      id: 1,
      title: "1. Information We Collect",
      content: "We collect personal information necessary to provide our rental agreement and deposit management services. This includes: (a) Identity information: Full name, phone number, email address; (b) Verification data: Aadhaar number, PAN, and ID proofs (collected only during KYC verification); (c) Property information: Property address, property photos, lease details; (d) Transaction information: Deposit amounts, payment details, transaction history; (e) Agreement data: Signatures, agreement terms, and dispute information. We collect only the minimum data required to provide our services."
    },
    {
      id: 2,
      title: "2. How We Use Your Information",
      content: "Your information is used solely for legitimate business purposes: (a) Creating and managing rental agreements with digital signatures; (b) Processing and holding rental deposits in protected accounts; (c) Resolving disputes through our AI and human review processes; (d) Sending transactional notifications and important updates; (e) Complying with legal and regulatory requirements; (f) Improving our platform and services; (g) Preventing fraud and maintaining security. We do NOT sell your personal information to third parties, use it for marketing or advertising purposes, or share it with unrelated companies."
    },
    {
      id: 3,
      title: "3. Your Data Rights",
      content: "Under India's Digital Personal Data Protection Act 2023 and other applicable laws, you have the following rights: (a) Right to access: Request a copy of all personal information we hold about you; (b) Right to correct: Update or correct any inaccurate information; (c) Right to delete: Request deletion of your account and personal information; (d) Right to withdraw consent: Withdraw your consent to data processing at any time; (e) Right to data portability: Request your data in a portable format. You can exercise these rights through your account Settings or by emailing privacy@orello.in."
    },
    {
      id: 4,
      title: "4. Data Retention & Deletion",
      content: "We retain different types of data for different periods: (a) Active account data: Retained while your account is active; (b) After account deletion: Personally identifiable information (PII) is deleted within 7 days of request; (c) Financial records: Retained for 7 years as per tax and legal compliance requirements; (d) Agreement PDFs: Retained in anonymised form permanently for legal and dispute resolution purposes; (e) Audit logs: Retained for 2 years for security and fraud prevention. Anonymised and aggregated data may be retained indefinitely for analytics purposes."
    },
    {
      id: 5,
      title: "5. DPDP Act Compliance",
      content: "Orello complies fully with India's Digital Personal Data Protection Act 2023. We obtain your explicit consent at signup for data collection and processing. You can withdraw this consent at any time by deleting your account. We have appointed a Data Protection Officer (DPO) to oversee compliance. Our data processing practices include: (a) Minimization of data collection; (b) Security measures to prevent unauthorized access; (c) Regular privacy audits; (d) Transparent communication about data usage; (e) Prompt response to data access requests."
    },
    {
      id: 6,
      title: "6. Data Security",
      content: "We implement industry-standard security measures to protect your data: (a) Encryption: Data is encrypted in transit (HTTPS/TLS) and at rest (AES-256); (b) Access control: Only authorized personnel can access sensitive data; (c) Regular audits: We conduct regular security assessments and penetration testing; (d) Secure integrations: Third-party integrations (payment gateways, KYC providers) follow strict security standards; (e) Breach notification: In case of any data breach, we will notify affected users within 48 hours as required by law. However, no system is 100% secure, and you acknowledge this risk when using our platform."
    },
    {
      id: 7,
      title: "7. Cookies & Tracking",
      content: "Orello uses cookies and similar tracking technologies to: (a) Maintain your login session; (b) Remember your preferences; (c) Analyze platform usage and improve features; (d) Prevent fraud and maintain security. Essential cookies are necessary for the platform to function. You can disable non-essential cookies through your browser settings, but this may limit functionality. We do NOT use tracking pixels for cross-site user profiling. Third-party services (analytics, payment processing) may also use cookies per their privacy policies."
    },
    {
      id: 8,
      title: "8. Third-Party Sharing",
      content: "We share your information only when necessary: (a) With payment processors: To process deposits and payments securely; (b) With KYC providers: For identity verification (Aadhaar, PAN verification); (c) With legal authorities: When required by law or court orders; (d) With dispute resolution experts: Only the necessary information to resolve disputes. We DO NOT share your data with advertisers, marketers, or other unrelated third parties. All third parties are bound by strict confidentiality agreements and data processing agreements."
    },
    {
      id: 9,
      title: "9. International Data Transfer",
      content: "Orello's services are hosted in India and primarily process data within India. If we transfer data internationally (for backups or disaster recovery), we do so only to countries with adequate data protection laws or with explicit safeguards. You will be notified of any such transfers. As an individual using Orello, by accepting this privacy policy, you consent to the processing and storage of your information as described herein."
    },
    {
      id: 10,
      title: "10. Changes to Privacy Policy",
      content: "We may update this privacy policy periodically to reflect changes in our practices or applicable laws. Significant changes will be communicated via email at least 30 days in advance. Your continued use of Orello after such changes constitutes acceptance of the updated policy. It is your responsibility to review this policy regularly. For major changes, we may require explicit re-consent. The current version date is shown at the top of this policy."
    },
    {
      id: 11,
      title: "11. Contact Us",
      content: "If you have questions about our privacy practices, data handling, or wish to exercise your data rights, please contact us: Email: privacy@orello.in; Data Protection Officer: dpo@orello.in; Mailing Address: Orello, Bangalore, India. We typically respond to privacy inquiries within 7 business days. For concerns that cannot be resolved, you may file a complaint with the local data protection authority in your jurisdiction."
    },
    {
      id: 12,
      title: "12. Grievance Redressal",
      content: "Orello is committed to addressing privacy concerns promptly. If you believe your privacy rights have been violated: (a) Contact our privacy team at privacy@orello.in with details of your concern; (b) We will acknowledge receipt within 48 hours and investigate within 30 days; (c) We will take corrective action if a violation is confirmed; (d) If unsatisfied with our response, you may escalate to the Data Protection Officer (dpo@orello.in) or relevant data protection authorities. We do not penalize users for filing legitimate privacy complaints."
    }
  ];

  return (
    <div style={{ background: "#080808", color: "#F5F5F5", minHeight: "100vh" }}>
      <Header />

      {/* Hero Section */}
      <section className="py-24 px-6" style={{ background: "linear-gradient(135deg, rgba(249,115,22,0.08), rgba(139,92,246,0.08))" }}>
        <div className="max-w-4xl mx-auto">
          <h1 className="font-display font-bold mb-4" style={{ fontSize: "clamp(2.5rem, 5vw, 3.5rem)", color: "#F5F5F5" }}>
            Privacy Policy
          </h1>
          <p style={{ fontSize: "1.05rem", color: "#D1D5DB", lineHeight: 1.7, marginBottom: "1rem" }}>
            Last updated: June 2026
          </p>
          <p style={{ fontSize: "1rem", color: "#A3A3A3" }}>
            Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-3">
            {sections.map((section) => (
              <div
                key={section.id}
                style={{
                  border: "1px solid rgba(255,255,255,0.06)",
                  borderRadius: "12px",
                  background: "rgba(255,255,255,0.02)",
                  overflow: "hidden",
                  transition: "all 0.3s ease"
                }}
              >
                <button
                  onClick={() => toggleSection(section.id)}
                  style={{
                    width: "100%",
                    padding: "1.5rem",
                    textAlign: "left",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: expanded === section.id ? "rgba(249,115,22,0.04)" : "transparent",
                    border: "none",
                    cursor: "pointer",
                    transition: "all 0.2s ease"
                  }}
                  onMouseEnter={(e) => e.target.style.background = "rgba(249,115,22,0.04)"}
                  onMouseLeave={(e) => e.target.style.background = expanded === section.id ? "rgba(249,115,22,0.04)" : "transparent"}
                >
                  <h3 style={{
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    color: "#F5F5F5",
                    fontFamily: "'Poppins', sans-serif"
                  }}>
                    {section.title}
                  </h3>
                  <ChevronDown
                    style={{
                      width: 20,
                      height: 20,
                      color: "#F97316",
                      flexShrink: 0,
                      transition: "transform 0.3s ease",
                      transform: expanded === section.id ? "rotate(180deg)" : "rotate(0deg)"
                    }}
                  />
                </button>

                {expanded === section.id && (
                  <div style={{
                    padding: "0 1.5rem 1.5rem 1.5rem",
                    borderTop: "1px solid rgba(255,255,255,0.04)",
                    background: "rgba(249,115,22,0.02)"
                  }}>
                    <p style={{
                      fontSize: "0.95rem",
                      color: "#D1D5DB",
                      lineHeight: 1.8
                    }}>
                      {section.content}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Contact Section */}
          <div style={{
            marginTop: "3rem",
            padding: "2rem",
            borderRadius: "12px",
            background: "linear-gradient(135deg, rgba(249,115,22,0.08), rgba(139,92,246,0.08))",
            border: "1px solid rgba(249,115,22,0.12)"
          }}>
            <h3 style={{ fontSize: "1.1rem", fontWeight: 600, color: "#F5F5F5", marginBottom: "0.5rem" }}>
              Privacy Questions?
            </h3>
            <p style={{ fontSize: "0.95rem", color: "#D1D5DB" }}>
              Contact our privacy team at{" "}
              <a href="mailto:privacy@orello.in" style={{ color: "#F97316", textDecoration: "none", fontWeight: 600 }}>
                privacy@orello.in
              </a>
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}